import React, { useContext } from 'react';
import useTranslations from '../../../hooks/useTranslations';
import MenuItem from '@material-ui/core/MenuItem';
import Menu from '@material-ui/core/Menu';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '../../Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import RemoveIcon from '@material-ui/icons/Remove';
import IconButton from '@material-ui/core/IconButton';

import styled from 'styled-components/macro';
import FieldsQueryBuilderEngine from '../engine/FieldsQueryBuilderEngine';
import { AutomationsContex } from '../../engine/FormBuilderAutomationsEngine';
import Select from '../../engine/SelectEngine';
import Animation from '../../Animation';
import FlaggingQueryBuilderEngine from '../engine/FlaggingQueryBuilderEngine';
import StatusQueryBuilderEngine from '../engine/StatusQueryBuilderEngine';
import SubmitterQueryBuilderEngine from '../engine/SubmitterQueryBuilderEngine';
import AdminQueryBuilderEngine from '../engine/AdminQueryBuilderEngine';
import Colors from '../../../internal-components/Colors';
import DateRangeQueryBuilderEngine from '../engine/DateRangeQueryBuilderEngine';

export const Options = {
  FIELDS: 'fields',
  FLAGGED: 'flagged',
  STATUS: 'status',
  ASSIGNED_USER: 'user',
  RESPONSIBLE_ADMIN: 'admin',
  DATE_RANGE: 'date_range'
};

const AddActionBtn = styled(Button)`
  && {
    margin-right: auto;
    margin-left: 0;
  }
`;

const ConditionsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Spacer = styled.span`
  margin-right: ${({ $space }) => ($space == 'and' ? '3rem' : '2rem')};
`;

export const ConditionBlock = styled.div`
  position: relative;
  opacity: ${({ $marked }) => ($marked ? '0.4' : '1')};
  transition: 0.3s;
  display: flex;
  align-items: baseline;
  ${({ $useSpacer }) =>
    $useSpacer &&
    `border-bottom: 1px solid ${Colors.OT_GREY}; padding: 0.8rem 0;`}
`;

const RemoveBtnPositioner = styled.div`
  position: absolute;
  z-index: 10;
  top: ${({ $useSpacer }) => ($useSpacer ? '20' : '10')}px;
  right: 0;
`;

const TextPrefixDiv = styled.span`
  font-weight: bold;
  margin-right: 0.4rem;
`;

const ConditionsQueryBuilderView = props => {
  const t = useTranslations();
  const { form } = props;
  return (
    <>
      <ConditionsContainer>
        {Object.entries(props.queries).map(([id, query], i) => (
          <Animation
            type="collapse"
            in={props.inDeleteSequence !== id}
            key={id}
            onExited={() => props.removeCondition(id)}
          >
            <ConditionBlock
              $marked={props.hoverOn === id}
              $useSpacer={props.useSpacers}
            >
              {!props.isHandlingSelf(props.getQueryType(id)) && (
                <RemoveBtnPositioner $useSpacer={props.useSpacers}>
                  <Tooltip arrow title="Remove condition">
                    <IconButton
                      size="small"
                      onMouseEnter={() => props.setHoverOn(id)}
                      onMouseLeave={() => props.setHoverOn(null)}
                      onClick={() => props.setInDeleteSequence(id)}
                    >
                      <RemoveIcon />
                    </IconButton>
                  </Tooltip>
                </RemoveBtnPositioner>
              )}

              {i === 0 ? (
                <>
                  {props.condtionsPrefixText && (
                    <TextPrefixDiv>{props.condtionsPrefixText}</TextPrefixDiv>
                  )}
                  {/* get extra spacing only if we don't have a sentence prefix or if there are multiple conditions */}
                  {Object.keys(props.queries).length > 1 &&
                  !props.condtionsPrefixText ? (
                    <Spacer $space={props.queryOp} />
                  ) : null}
                </>
              ) : (
                <AndOr value={props.queryOp} update={props.setQueryOp} />
              )}
              {(() => {
                switch (props.getQueryType(id)) {
                  case Options.FIELDS:
                    return (
                      <FieldsQueryBuilderEngine
                        query={query}
                        form={form}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  case Options.FLAGGED:
                    return (
                      <FlaggingQueryBuilderEngine
                        query={query}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  case Options.STATUS:
                    return (
                      <StatusQueryBuilderEngine
                        query={query}
                        form={form}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  case Options.ASSIGNED_USER:
                    return (
                      <SubmitterQueryBuilderEngine
                        query={query}
                        form={form}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  case Options.RESPONSIBLE_ADMIN:
                    return (
                      <AdminQueryBuilderEngine
                        query={query}
                        form={form}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  case Options.DATE_RANGE:
                    return (
                      <DateRangeQueryBuilderEngine
                        query={query}
                        form={form}
                        update={qBlock => props.updateQuery(id, qBlock)}
                      />
                    );
                  default:
                    break;
                }
              })()}
            </ConditionBlock>
          </Animation>
          // check based on query type with function!
        ))}
      </ConditionsContainer>

      <AddActionBtn
        // disabled={props.actions.length >= Object.keys(Actions).length}
        startIcon={<AddCircleOutlineIcon />}
        variant="text"
        onClick={props.onMenuOpen}
      >
        {props.addConditionLabelText
          ? props.addConditionLabelText
          : t('automaions.wizard.conditions.new')}
      </AddActionBtn>
      <Menu
        anchorEl={props.anchorBtn}
        keepMounted
        open={Boolean(props.anchorBtn)}
        onClose={props.onMenuClose}
      >
        {!props.filter.includes(Options.FIELDS) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.FIELDS);
            }}
          >
            {t('automaions.wizard.conditions.fields')}
          </MenuItem>
        )}
        {!props.filter.includes(Options.FLAGGED) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.FLAGGED);
            }}
          >
            {t('automaions.wizard.conditions.flagged')}
          </MenuItem>
        )}
        {!props.filter.includes(Options.STATUS) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.STATUS);
            }}
          >
            {t('automaions.wizard.conditions.status')}
          </MenuItem>
        )}
        {!props.filter.includes(Options.ASSIGNED_USER) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.ASSIGNED_USER);
            }}
          >
            {t('automaions.wizard.conditions.assigned_user')}
          </MenuItem>
        )}
        {!props.filter.includes(Options.RESPONSIBLE_ADMIN) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.RESPONSIBLE_ADMIN);
            }}
          >
            {t('automaions.wizard.conditions.admin')}
          </MenuItem>
        )}
        {!props.filter.includes(Options.DATE_RANGE) && (
          <MenuItem
            onClick={() => {
              props.onMenuSelect(Options.DATE_RANGE);
            }}
          >
            {t('automaions.wizard.conditions.date_range')}
          </MenuItem>
        )}
      </Menu>
    </>
  );
};

export const AndOr = ({ value = '', update = () => {}, hierarchyLv = 0 }) => {
  const selectStyle = `margin-right: 0.8rem !important;
   color: ${Colors.PRIMARY} !important;
   font-weight: 600 !important;
   `;

  return (
    <Select
      noArrow
      variant="inline"
      data-testid="logicbuilder-select-and-or"
      value={value}
      onChange={update}
      options={[
        {
          text: 'and',
          value: 'and',
          'data-testid': 'logicbuilder-select-and-or-option-and'
        },
        {
          text: 'or',
          value: 'or',
          'data-testid': 'logicbuilder-select-and-or-option-or'
        }
      ]}
      customCSS={selectStyle}
      disableUnderline
    />
  );
};

export default ConditionsQueryBuilderView;
